import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  Beneficiaries,
  Beneficiary,
  BeneficiaryAge,
} from '../../Beneficiary/utils/beneficiaryInterfaces';

export const beneficiaryCategories: (keyof Beneficiaries)[] = [
  'age',
  'ethnic',
  'gender',
  'health',
  'social',
  'work',
];

const getHeading = (category: keyof Beneficiaries): JSX.Element | string => {
  switch (category) {
    case 'age':
      return 'Age groups';
    case 'ethnic':
      return 'Ethnic and racial groups';
    case 'gender':
      return <>Gender &amp; Sexual Identity</>;
    case 'health':
      return 'Health';
    case 'social':
      return <>Social &amp; Economic Status</>;
    case 'work':
      return <>Work Status &amp; Occupations</>;
    default:
      return 'Beneficiaries';
  }
};

export const getBeneficiaryNameLabel = (age: BeneficiaryAge): string => {
  switch (age) {
    case 'Adults':
      return 'All Adults';

    case 'Nineteen and older':
      return 'Adults (19+)';

    case 'Seniors':
      return 'Seniors (64+)';

    case 'Children and youth':
      return 'All Children';

    case 'Children':
      return 'Children (0-9)';

    case 'Adolescents':
      return 'Youth (10+)';

    default:
      return 'All Ages';
  }
};

interface BeneficiaryCardProps {
  category: keyof Beneficiaries;
  beneficiaries: Beneficiaries;
}

const StyledDiv = styled.div`
  margin-bottom: ${SPACERS.large};
  .card-details-heading {
    margin-bottom: ${SPACERS.medium};
  }
  .age-text {
    padding-left: ${SPACERS.medium};
  }
  .card-row {
    width: 250px;
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    justify-content: space-between;
    margin-bottom: ${SPACERS.small};
    padding-left: ${SPACERS.medium};
    .card-row-text {
      flex: 1;
    }
  }
`;

const BeneficiaryCard: React.FC<BeneficiaryCardProps> = ({
  category,
  beneficiaries,
}) => {
  const beneficiary = beneficiaries[category];
  if (!beneficiary?.categories?.length) return null;
  const heading = getHeading(category);
  if (category === 'age' && beneficiaries.age) {
    const adult: Beneficiary<BeneficiaryAge>[] = [];
    const children: Beneficiary<BeneficiaryAge>[] = [];
    beneficiaries.age.categories.forEach(a => {
      if (
        a.name === 'Adults' ||
        a.name === 'Nineteen and older' ||
        a.name === 'Seniors'
      ) {
        adult.push(a);
      } else {
        children.push(a);
      }
    });
    return (
      <StyledDiv className="beneficiary-card">
        <TrueImpactText
          variant="heading-subheading"
          className="card-details-heading"
        >
          {heading}
        </TrueImpactText>
        <TrueImpactText variant="heading-label" className="age-text">
          Adults
        </TrueImpactText>
        {adult.map((c, i) => {
          if (c.percentage) {
            return (
              <div className="card-row" key={i}>
                <TrueImpactText variant="body-bold" className="card-row-text">
                  {getBeneficiaryNameLabel(c.name)}
                </TrueImpactText>
                <TrueImpactText>{c.percentage}%</TrueImpactText>
              </div>
            );
          }
        })}
        <TrueImpactText variant="heading-label" className="age-text">
          Children & Youth
        </TrueImpactText>
        {children.map((c, i) => {
          if (c.percentage) {
            return (
              <div className="card-row" key={i}>
                <TrueImpactText variant="body-bold" className="card-row-text">
                  {getBeneficiaryNameLabel(c.name)}
                </TrueImpactText>
                <TrueImpactText>{c.percentage}%</TrueImpactText>
              </div>
            );
          }
        })}
      </StyledDiv>
    );
  }

  return (
    <StyledDiv className="beneficiary-card">
      <TrueImpactText
        variant="heading-subheading"
        className="card-details-heading"
      >
        {heading}
      </TrueImpactText>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {beneficiary.categories.map((c: Beneficiary<any>, i: number) => {
        if (c.percentage) {
          return (
            <div className="card-row" key={i}>
              <TrueImpactText variant="body-bold" className="card-row-text">
                {c.name}
              </TrueImpactText>
              <TrueImpactText>{c.percentage}%</TrueImpactText>
            </div>
          );
        }
      })}
    </StyledDiv>
  );
};

export default BeneficiaryCard;
